import { SchadenbeteiligterKontakt, SchadenbeteiligterKontaktStatus } from '@gsm/entities';
import { AND_OPERATOR, DYNAMIC_FORM_CONTROL_TYPE_GROUP, DYNAMIC_FORM_CONTROL_TYPE_INPUT, DYNAMIC_FORM_CONTROL_TYPE_SELECT, DYNAMIC_FORM_CONTROL_TYPE_TEXTAREA, DynamicFormOptionConfig, MATCH_ENABLED, MATCH_HIDDEN, MATCH_REQUIRED, MATCH_VISIBLE } from '@ng-dynamic-forms/core';
import { compareProps } from '../../helpers/compare-props.helper';
import { getDisplayName } from '../../helpers/display-name.helper';
import { AnyFormControlModel } from '../shared-models/any-form-control.model';
import { KontaktFormType } from './schadenbeteiligter-kontakt-erfassen.model';

interface SchadenbeteiligterKontaktErfassenConfig {
  additionalOptions: DynamicFormOptionConfig<SchadenbeteiligterKontaktStatus>[];
  selectLabel: string;
  erfassenLabel: string;
  emailLabel: string;
  telefonNrLabel: string;
  telefonNrRequired: boolean;
}

const GERAETE_STANDORT_CONFIG: SchadenbeteiligterKontaktErfassenConfig = {
  additionalOptions: [
    {
      label: 'Sämtliche Geräte sind nicht mehr vorhanden (entsorgt)',
      value: 'KEINER'
    }
  ],
  selectLabel:       'Standort',
  erfassenLabel:     'Neuen Standort erfassen',
  emailLabel:        'E-Mail für Besichtigungstermin',
  telefonNrLabel:    'Telefonnummer für Besichtigungstermin',
  telefonNrRequired: true
};

const DEFAULT_CONFIG: SchadenbeteiligterKontaktErfassenConfig = {
  additionalOptions: [],
  selectLabel:       'Kontaktdaten',
  erfassenLabel:     'Kontaktdaten erfassen',
  emailLabel:        'E-Mail',
  telefonNrLabel:    'Telefonnummer',
  telefonNrRequired: false
};

const WHEN_KEIN_KONTAKT = [
  {
    id:    'schadenbeteiligter_kontakt',
    value: undefined
  },
  {
    id:    'schadenbeteiligter_kontakt',
    value: null
  },
  {
    id:    'schadenbeteiligter_kontakt',
    value: 'NACHGEBRACHT'
  },
  {
    id:    'schadenbeteiligter_kontakt',
    value: 'UNBEKANNT'
  },
  {
    id:    'schadenbeteiligter_kontakt',
    value: 'KEINER'
  },
  {
    id:    'schadenbeteiligter_kontakt',
    value: 'SIEHE_BEILAGE'
  }
];

export function SCHADENBETEILIGTER_KONTAKT_ERFASSEN_TEMPLATE_FACTORY(
  kontaktList: SchadenbeteiligterKontakt[],
  kontaktFormType: KontaktFormType,
  paperProcessOverride: boolean
): AnyFormControlModel[] {
  const {
    additionalOptions,
    selectLabel,
    erfassenLabel,
    emailLabel,
    telefonNrLabel,
    telefonNrRequired
  } =
    kontaktFormType === KontaktFormType.GeraeteStandort ? GERAETE_STANDORT_CONFIG : DEFAULT_CONFIG;

  return [
    {
      id:            'schadenbeteiligter_kontakt',
      type:          DYNAMIC_FORM_CONTROL_TYPE_SELECT,
      label:         selectLabel,
      required:      true,
      compareWithFn: compareProps.bind(null, ['kundeId']),
      options:       [
        ...(paperProcessOverride ? [
          {
            label: 'Siehe Beilage',
            value: 'SIEHE_BEILAGE'
          }
        ] : []),
        ...kontaktList.map(value => ({
          label: getDisplayName([
            value.vorname,
            value.nachname
          ]),
          value
        })),
        {
          label: erfassenLabel,
          value: 'ERFASSEN'
        },
        ...additionalOptions
      ],
      errorMessages: {
        required: '{{ label }} ist ein Pflichtfeld.'
      }
    },
    {
      id:        'schadenbeteiligter_name',
      type:      DYNAMIC_FORM_CONTROL_TYPE_GROUP,
      hidden:    true,
      relations: [
        {
          match: MATCH_VISIBLE,
          when:  [
            {
              id:    'schadenbeteiligter_kontakt',
              value: 'ERFASSEN'
            }
          ]
        }
      ],
      group:     [
        {
          id:            'vorname',
          type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
          placeholder:   'Bitte geben Sie den Vornamen ein.',
          label:         'Vorname',
          validators:    {
            maxLength: 25
          },
          errorMessages: {
            alphanum:  'Bitte geben Sie einen gültigen Vornamen ein',
            maxLength: 'Bitte geben Sie maximal 25 Zeichen ein.'
          },
          value:         ''
        },
        {
          id:            'nachname',
          type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
          placeholder:   'Bitte geben Sie den Nachnamen ein.',
          label:         'Nachname',
          required:      true,
          validators:    {
            minLength: 2,
            required:  null,
            maxLength: 60
          },
          errorMessages: {
            minLength: 'Bitte geben Sie einen gültigen Nachnamen ein.',
            alphanum:  'Bitte geben Sie einen gültigen Nachnamen ein.',
            required:  '{{ label }} ist ein Pflichtfeld.',
            maxLength: 'Bitte geben Sie maximal 60 Zeichen ein.'
          }
        }
      ]
    },
    {
      id:        'schadenbeteiligter_adresse',
      type:      DYNAMIC_FORM_CONTROL_TYPE_GROUP,
      hidden:    true,
      disabled:  true,
      relations: [
        {
          match: MATCH_HIDDEN,
          when:  WHEN_KEIN_KONTAKT
        },
        {
          match:    MATCH_ENABLED,
          operator: AND_OPERATOR,
          when:     [
            {
              id:    'schadenbeteiligter_kontakt',
              value: 'ERFASSEN'
            },
            // prevent enabling if schadenbeteiligter kontakt is disabled:
            {
              id:     'schadenbeteiligter_kontakt',
              status: 'VALID'
            }
          ]
        }
      ],
      group:     [
        {
          id:            'plz',
          type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
          placeholder:   'Bitte geben Sie eine Postleitzahl an.',
          label:         'PLZ',
          validators:    {
            maxLength: 10,
            alphanum:  {
              letterRequired: false
            }
          },
          errorMessages: {
            alphanum:  'Bitte geben Sie eine gültige {{label}} ein.',
            maxLength: 'Bitte geben Sie maximal 10 Zeichen ein.'
          }
        },
        {
          id:            'ort',
          type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
          placeholder:   'Bitte geben Sie einen Ort an.',
          label:         'Ort',
          validators:    {
            minLength: 2,
            maxLength: 30
          },
          errorMessages: {
            alphanum:  'Bitte geben Sie einen gültigen {{label}} ein.',
            minLength: 'Bitte geben Sie mindestens 2 Zeichen ein.',
            required:  '{{ label }} ist ein Pflichtfeld.',
            maxLength: 'Bitte geben Sie maximal 30 Zeichen ein.'
          },
          relations:     [
            {
              match: MATCH_REQUIRED,
              when:  [
                {
                  rootPath: 'schadenbeteiligter_kontakt',
                  value:    'ERFASSEN'
                }
              ]
            }
          ]
        },
        {
          id:            'strasse',
          type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
          placeholder:   'Bitte geben Sie eine Straße an.',
          label:         'Straße',
          validators:    {
            minLength: 2,
            alphanum:  null,
            maxLength: 45
          },
          errorMessages: {
            alphanum:  'Bitte geben Sie eine gültige {{label}} ein.',
            minLength: 'Bitte geben Sie mindestens 2 Zeichen ein.',
            required:  '{{ label }} ist ein Pflichtfeld.',
            maxLength: 'Bitte geben Sie maximal 45 Zeichen ein.'
          },
          relations:     [
            {
              match: MATCH_REQUIRED,
              when:  [
                {
                  rootPath: 'schadenbeteiligter_kontakt',
                  value:    'ERFASSEN'
                }
              ]
            }
          ]
        },
        {
          id:            'hausNummer',
          type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
          placeholder:   'Bitte geben Sie eine Hausnummer an.',
          label:         'Hausnummer',
          validators:    {
            alphanum:  {
              letterRequired: false
            },
            maxLength: 7
          },
          errorMessages: {
            alphanum:  'Bitte geben Sie eine gültige {{label}} ein.',
            maxLength: 'Bitte geben Sie maximal 7 Zeichen ein.'
          }
        }
      ]
    },
    {
      id:        'schadenbeteiligter_kontakt_daten',
      type:      DYNAMIC_FORM_CONTROL_TYPE_GROUP,
      hidden:    true,
      relations: [
        {
          match: MATCH_HIDDEN,
          when:  WHEN_KEIN_KONTAKT
        }
      ],
      group:     [
        {
          id:            'email',
          type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
          placeholder:   'Bitte geben Sie eine E-Mail an.',
          label:         emailLabel,
          validators:    {
            email:     null,
            maxLength: 300
          },
          errorMessages: {
            email:     'Bitte geben Sie eine gültige {{label}} ein.',
            maxLength: 'Bitte geben Sie maximal 300 Zeichen ein.'
          }
        },
        {
          id:            'telefonNr',
          type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
          placeholder:   'Bitte geben Sie eine Telefonnummer an.',
          label:         telefonNrLabel,
          required:      telefonNrRequired,
          validators:    {
            phone:     null,
            maxLength: 300
          },
          errorMessages: {
            phone:     'Bitte geben Sie eine gültige {{label}} ein.',
            required:  '{{ label }} ist ein Pflichtfeld.',
            maxLength: 'Bitte geben Sie maximal 300 Zeichen ein.'
          }
        },
        {
          id:            'notiz',
          type:          DYNAMIC_FORM_CONTROL_TYPE_TEXTAREA,
          placeholder:   'Notiz hinzufügen',
          label:         'Notiz',
          validators:    {
            maxLength: 300
          },
          errorMessages: {
            maxLength: 'Bitte geben Sie maximal 300 Zeichen ein.'
          }
        }
      ]
    }
  ];
}
